import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  addDays, addWeeks, addMonths, format
} from 'date-fns';
import { Button } from '@/shared/ui/button';
import { ChevronLeft, ChevronRight, CalendarDays, Info } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { DateTimePicker } from '@/shared/ui/datetime-picker';
import { Calendar } from '@/shared/ui/calendar';
import { Label } from '@/shared/ui/label';
import { cn } from '@/shared/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Separator } from '@/shared/ui/separator';
import { Card, CardContent } from '@/shared/ui/card';

type TimePreset = 'day' | 'week' | 'month';

// A more descriptive state object for the time selection
export type TimeSelection = {
  mode: 'preset';
  preset: TimePreset;
  refDate: Date;
} | {
  mode: 'custom';
  start: Date;
  end: Date;
};

interface TimeRangeSelectorProps {
  value: TimeSelection;
  onChange: (selection: TimeSelection) => void;
  className?: string;
  fluxQuery?: string;
  inlinePresetNav?: boolean;
}

const getPresetRange = (preset: TimePreset, date: Date): { start: Date, end: Date } => {
  switch (preset) {
    case 'day':
      return { start: startOfDay(date), end: endOfDay(date) };
    case 'week':
      return { start: startOfWeek(date, { weekStartsOn: 1 }), end: endOfWeek(date, { weekStartsOn: 1 }) };
    case 'month':
      return { start: startOfMonth(date), end: endOfMonth(date) };
    default:
      return { start: startOfDay(date), end: endOfDay(date) };
  }
};

const formatPresetDate = (preset: TimePreset, range: { start: Date, end: Date }): string => {
  if (!range.start || !range.end) return "Loading...";
  switch (preset) {
    case 'day':
      return format(range.start, 'PPP');
    case 'week':
      return `${format(range.start, 'MMM d')} - ${format(range.end, 'MMM d, yyyy')}`;
    case 'month':
      return format(range.start, 'MMMM yyyy');
    default:
      return '';
  }
}

export const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  value,
  onChange,
  className,
  fluxQuery,
  inlinePresetNav = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draftSelection, setDraftSelection] = useState<TimeSelection>(value);

  useEffect(() => {
    if (isOpen) {
      setDraftSelection(value);
    }
  }, [isOpen, value]);

  const calculatedDraftRange = useMemo(() => {
    if (draftSelection.mode === 'preset') {
      return getPresetRange(draftSelection.preset, draftSelection.refDate);
    }
    return { start: draftSelection.start, end: draftSelection.end };
  }, [draftSelection]);

  const handleApply = () => {
    if (draftSelection.mode === 'custom' && draftSelection.start > draftSelection.end) {
      console.error("Invalid custom time range: start date is after end date.");
      return;
    }
    onChange(draftSelection);
    setIsOpen(false);
  };

  const handleModeChange = (newMode: 'preset' | 'custom') => {
    if (newMode === draftSelection.mode) return;
    if (newMode === 'preset') {
      setDraftSelection({ mode: 'preset', preset: 'day', refDate: new Date() });
    } else {
      setDraftSelection({
        mode: 'custom',
        start: calculatedDraftRange.start,
        end: calculatedDraftRange.end,
      });
    }
  };

  const handlePresetChange = (newPreset: TimePreset) => {
    if (draftSelection.mode === 'preset') {
      setDraftSelection({ ...draftSelection, preset: newPreset });
    }
  };

  const handlePresetNav = (direction: 'prev' | 'next') => {
    if (draftSelection.mode !== 'preset') return;
    const mover = direction === 'prev' ? -1 : 1;
    let newRefDate;
    switch (draftSelection.preset) {
      case 'day': newRefDate = addDays(draftSelection.refDate, mover); break;
      case 'week': newRefDate = addWeeks(draftSelection.refDate, mover); break;
      case 'month': newRefDate = addMonths(draftSelection.refDate, mover); break;
    }
    setDraftSelection({ ...draftSelection, refDate: newRefDate });
  };

  const handleInlinePresetNav = (direction: 'prev' | 'next') => {
    if (value.mode !== 'preset') return;
    const mover = direction === 'prev' ? -1 : 1;
    let newRefDate;
    switch (value.preset) {
      case 'day': newRefDate = addDays(value.refDate, mover); break;
      case 'week': newRefDate = addWeeks(value.refDate, mover); break;
      case 'month': newRefDate = addMonths(value.refDate, mover); break;
    }
    onChange({ mode: 'preset', preset: value.preset, refDate: newRefDate });
  };

  const handleReset = useCallback(() => {
    onChange({ mode: 'preset', preset: 'day', refDate: new Date() });
    setIsOpen(false);
  }, [onChange]);

  const summaryText = useMemo(() => {
    if (value.mode === 'preset') {
      const range = getPresetRange(value.preset, value.refDate);
      return formatPresetDate(value.preset, range);
    }
    return `${format(value.start, 'MMM d, yy HH:mm')} - ${format(value.end, 'MMM d, yy HH:mm')}`;
  }, [value]);

  const inlineNavDisabled = value.mode !== 'preset';

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {inlinePresetNav && (
        <Button
          variant="outline"
          size="icon"
          className="shrink-0"
          onClick={() => handleInlinePresetNav('prev')}
          disabled={inlineNavDisabled}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      )}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full md:w-auto justify-start text-left font-normal">
            <CalendarDays className="mr-2 h-4 w-4" />
            <span className="flex-1">{summaryText}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-4" align="end">
          <Tabs value={draftSelection.mode} onValueChange={(v) => handleModeChange(v as 'preset' | 'custom')}>
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="preset">Presets</TabsTrigger>
              <TabsTrigger value="custom">Custom</TabsTrigger>
            </TabsList>
            <TabsContent value="preset" className="mt-0 space-y-4">
              {draftSelection.mode === 'preset' && (
                <>
                  <Select value={draftSelection.preset} onValueChange={(v) => handlePresetChange(v as TimePreset)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a preset" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day">Day</SelectItem>
                      <SelectItem value="week">Week</SelectItem>
                      <SelectItem value="month">Month</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className={cn("flex items-center gap-2", inlinePresetNav && "justify-center")}>
                    {!inlinePresetNav && (
                      <Button variant="outline" size="icon" onClick={() => handlePresetNav('prev')}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                    )}
                    <div className="flex-1 text-center font-medium text-sm border rounded-md h-10 flex items-center justify-center bg-muted/50 px-3">
                      {formatPresetDate(draftSelection.preset, calculatedDraftRange)}
                    </div>
                    {!inlinePresetNav && (
                      <Button variant="outline" size="icon" onClick={() => handlePresetNav('next')}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {draftSelection.preset === 'day' && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full justify-start">
                          <CalendarDays className="mr-2 h-4 w-4" />
                          Pick a date
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={draftSelection.refDate}
                          onSelect={(date) =>
                            date && setDraftSelection({ ...draftSelection, refDate: date })
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                </>
              )}
            </TabsContent>
            <TabsContent value="custom" className="mt-0">
              {draftSelection.mode === 'custom' && (
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date & Time</Label>
                    <DateTimePicker
                      date={draftSelection.start}
                      setDate={(date) => date && setDraftSelection({ ...draftSelection, start: date })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date & Time</Label>
                    <DateTimePicker
                      date={draftSelection.end}
                      setDate={(date) => date && setDraftSelection({ ...draftSelection, end: date })}
                    />
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
          <Separator className="my-4" />
          <div className="flex justify-between items-center">
            {fluxQuery ? (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="link" size="sm" className="p-0 h-auto text-muted-foreground gap-1">
                    <Info className="h-3 w-3" />
                    <span className="text-xs">View Query</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-[400px] p-0">
                  <Card className="shadow-lg">
                    <CardContent className="p-0">
                      <pre className="text-xs bg-muted p-4 rounded-md overflow-x-auto">
                        <code>{fluxQuery}</code>
                      </pre>
                    </CardContent>
                  </Card>
                </PopoverContent>
              </Popover>
            ) : ( <div /> )}
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleReset}>
                Today
              </Button>
              <Button size="sm" onClick={handleApply}>Apply</Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {inlinePresetNav && (
        <Button
          variant="outline"
          size="icon"
          className="shrink-0"
          onClick={() => handleInlinePresetNav('next')}
          disabled={inlineNavDisabled}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};
